import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { UserDetailsView, UserDetailsViewSkeleton } from "@surgia/ui";
import { useGetUser } from "../../hooks/useGetUser";
import { RootState } from "../../store";
import { HUB_PROTECTED_ROUTES } from "../../routes";
import { formatDateWithMoment } from "@surgia/utils";

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const authState = useSelector((state: RootState) => state.auth);

  const tenant_id = authState.globalState.tenant_id;
  const userBranches = authState.globalState.user_branches || "";

  const {
    data: user,
    isLoading,
    error,
  } = useGetUser(
    {
      component_id:
        import.meta.env.VITE_KEYCLOAK_ISSUER?.includes("test") ||
        import.meta.env.VITE_KEYCLOAK_ISSUER?.includes("develop")
          ? 8
          : 6,
      tenant_id: tenant_id!,
      branches_ids: userBranches,
      user_id: userId!,
    },
    {
      enabled: !!(userId && tenant_id),
    }
  );

  const handleGoBack = () => {
    navigate(HUB_PROTECTED_ROUTES.USERS);
  };

  if (isLoading) {
    return <UserDetailsViewSkeleton />;
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-600 mb-2">
            Error loading user details
          </h3>
          <p className="text-gray-500 mb-4">
            {error?.message || "User not found"}
          </p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back to Users
          </button>
        </div>
      </div>
    );
  }

  // Format user roles for display
  const userRoles = user.role?.map((role: any) => role.name).join(", ") || "-";

  // Format user branches for display
  const userBranchNames =
    user.branches?.map((branch: any) => branch.name).join(", ") || "-";

  return (
    <UserDetailsView
      breadcrumbItems={[
        {
          label: t("Users", "Users"),
          onClick: handleGoBack,
          show: true,
        },
        {
          label: t("User details", "User details"),
          show: true,
        },
      ]}
      headerTitle={
        user.name ||
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        "Unknown User"
      }
      headerSubtitle={userId}
      accountDetails={{
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        phoneNumber: user.mobile,
        status: user.status,
        dateCreated: user.createdAt
          ? formatDateWithMoment(user.createdAt)
          : undefined,
        lastLogin: user.lastLogin
          ? formatDateWithMoment(
              user.lastLogin,
              "MMM DD, YYYY [at] HH:mm:ss A [UTC]"
            )
          : undefined,
        userId: userId,
      }}
      accessSettings={{
        role: userRoles,
        branch: userBranchNames,
      }}
      labels={{
        accountDetailsTitle: t("Account details", "Account details"),
        accessSettingsTitle: t("Access Settings", "Access Settings"),
        firstName: t("First name", "First name"),
        lastName: t("Last name", "Last name"),
        email: t("Email address", "Email address"),
        phoneNumber: t("Phone number", "Phone number"),
        status: t("Status", "Status"),
        dateCreated: t("Date created", "Date created"),
        lastLogin: t("Last login", "Last login"),
        role: t("Role", "Role"),
        branch: t("Branch", "Branch"),
      }}
    />
  );
};

export default UserDetails;
